import { parseMarkdownTable } from "./markdownBlockParser";
import { NormalizedTableBlock, NormalizedTableCell } from "./markdownDiffTypes";
import { renderTableCellDiff } from "./markdownInlineDiff";
import {
  createUniqueRowKeyMap,
  findSingleIdentifierColumn,
} from "./markdownTableMatcher";
import { wrapContentInSpan } from "./markdownTextDiff";

type RowAlignment = {
  currentRow: NormalizedTableCell[] | null;
  previousRow: NormalizedTableCell[] | null;
};

type ColumnAlignment = {
  currentIndex: number | null;
  previousIndex: number | null;
};

export const calculateTableDiffMarkdown = (
  currentTableMarkdown: string,
  previousTableMarkdown: string
): string => {
  const currentTable = parseMarkdownTable(currentTableMarkdown);
  const previousTable = parseMarkdownTable(previousTableMarkdown);

  const isCurrentEmpty = currentTableMarkdown.trim() === "";
  const isPreviousEmpty = previousTableMarkdown.trim() === "";
  const isHeaderless = currentTable.isHeaderless || previousTable.isHeaderless;

  if (isPreviousEmpty && !isCurrentEmpty) {
    return buildFullTableWithSpans(currentTable, true, false);
  }

  if (isCurrentEmpty && !isPreviousEmpty) {
    return buildFullTableWithSpans(previousTable, false, true);
  }

  const columnAlignment = alignTableColumns(previousTable, currentTable);
  const rows = alignTableRows(currentTable, previousTable);

  if (isHeaderless) {
    return rows.map((row) => buildDataRow(columnAlignment, row)).join("\n");
  }

  return [
    buildHeaderRow(currentTable, previousTable, columnAlignment),
    buildSeparatorRow(columnAlignment.length),
    ...rows.map((row) => buildDataRow(columnAlignment, row)),
  ].join("\n");
};

export const alignTableRows = (
  currentTable: NormalizedTableBlock,
  previousTable: NormalizedTableBlock
): RowAlignment[] => {
  const headerlessIdentifierPair = findHeaderlessIdentifierColumnPair(
    currentTable,
    previousTable
  );
  if (headerlessIdentifierPair) {
    return createIdentifierRowAlignment(
      currentTable,
      previousTable,
      headerlessIdentifierPair
    );
  }

  const identifierPair = findIdentifierColumnPair(currentTable, previousTable);
  if (!identifierPair) {
    return createPositionalRowAlignment(currentTable, previousTable);
  }

  return createIdentifierRowAlignment(
    currentTable,
    previousTable,
    identifierPair
  );
};

const createIdentifierRowAlignment = (
  currentTable: NormalizedTableBlock,
  previousTable: NormalizedTableBlock,
  identifierPair: { currentIndex: number; previousIndex: number }
): RowAlignment[] => {
  const currentRowMap = createUniqueRowKeyMap(
    currentTable.rowTexts,
    identifierPair.currentIndex
  );
  const previousRowMap = createUniqueRowKeyMap(
    previousTable.rowTexts,
    identifierPair.previousIndex
  );
  if (!currentRowMap || !previousRowMap) {
    return createPositionalRowAlignment(currentTable, previousTable);
  }

  const currentKeys = currentTable.rowTexts.map((row) =>
    normalizeRowKey(row[identifierPair.currentIndex] || "")
  );
  const previousKeys = previousTable.rowTexts.map((row) =>
    normalizeRowKey(row[identifierPair.previousIndex] || "")
  );
  const currentKeySet = new Set(currentKeys);
  const alignments: RowAlignment[] = [];
  let previousCursor = 0;

  currentTable.rows.forEach((currentRow, currentIndex) => {
    const currentKey = currentKeys[currentIndex];
    const previousRowEntry = previousRowMap.get(currentKey);

    if (!previousRowEntry) {
      alignments.push({ currentRow, previousRow: null });
      return;
    }

    while (previousCursor < previousRowEntry.index) {
      const previousKey = previousKeys[previousCursor];
      if (!currentKeySet.has(previousKey)) {
        alignments.push({
          currentRow: null,
          previousRow: previousTable.rows[previousCursor],
        });
      }
      previousCursor += 1;
    }

    alignments.push({
      currentRow,
      previousRow: previousTable.rows[previousRowEntry.index],
    });
    previousCursor = Math.max(previousCursor, previousRowEntry.index + 1);
  });

  while (previousCursor < previousTable.rows.length) {
    const previousKey = previousKeys[previousCursor];
    if (!currentKeySet.has(previousKey)) {
      alignments.push({
        currentRow: null,
        previousRow: previousTable.rows[previousCursor],
      });
    }
    previousCursor += 1;
  }

  return alignments;
};

const buildFullTableWithSpans = (
  table: NormalizedTableBlock,
  added: boolean,
  removed: boolean
): string => {
  const columnAlignment = Array.from(
    { length: table.columnCount },
    (_, index) => ({
      currentIndex: index,
      previousIndex: index,
    })
  );

  if (table.isHeaderless) {
    return table.rows
      .map(
        (row) =>
          `| ${columnAlignment
            .map(({ currentIndex }) =>
              renderWholeTableCell(
                currentIndex === null ? undefined : row[currentIndex],
                added,
                removed
              )
            )
            .join(" | ")} |`
      )
      .join("\n");
  }

  return [
    buildHeaderRow(
      added ? table : emptyTable(),
      removed ? table : emptyTable(),
      columnAlignment
    ),
    buildSeparatorRow(columnAlignment.length),
    ...table.rows.map(
      (row) =>
        `| ${columnAlignment
          .map(({ currentIndex }) =>
            renderWholeTableCell(
              currentIndex === null ? undefined : row[currentIndex],
              added,
              removed
            )
          )
          .join(" | ")} |`
    ),
  ].join("\n");
};

const buildHeaderRow = (
  currentTable: NormalizedTableBlock,
  previousTable: NormalizedTableBlock,
  columnAlignment: ColumnAlignment[]
): string =>
  `| ${columnAlignment
    .map(({ currentIndex, previousIndex }) =>
      renderTableCellDiff(
        previousIndex === null
          ? undefined
          : previousTable.headerCells[previousIndex],
        currentIndex === null
          ? undefined
          : currentTable.headerCells[currentIndex]
      )
    )
    .join(" | ")} |`;

const buildSeparatorRow = (columnCount: number): string =>
  `| ${Array.from({ length: columnCount }, () => "---").join(" | ")} |`;

const buildDataRow = (
  columnAlignment: ColumnAlignment[],
  rowAlignment: RowAlignment
): string =>
  `| ${columnAlignment
    .map(({ currentIndex, previousIndex }) =>
      renderTableCellDiff(
        previousIndex === null
          ? undefined
          : rowAlignment.previousRow?.[previousIndex],
        currentIndex === null
          ? undefined
          : rowAlignment.currentRow?.[currentIndex]
      )
    )
    .join(" | ")} |`;

const alignTableColumns = (
  previousTable: NormalizedTableBlock,
  currentTable: NormalizedTableBlock
): ColumnAlignment[] => {
  if (
    currentTable.columnCount === previousTable.columnCount &&
    currentTable.columnCount > 0
  ) {
    return Array.from({ length: currentTable.columnCount }, (_, index) => ({
      currentIndex: index,
      previousIndex: index,
    }));
  }

  const previousHeaders = previousTable.normalizedHeaders;
  const currentHeaders = currentTable.normalizedHeaders;

  if (currentHeaders.length === previousHeaders.length + 1) {
    const insertedIndex = findSingleInsertedHeaderIndex(
      previousHeaders,
      currentHeaders
    );
    if (insertedIndex !== null) {
      return currentHeaders.map((_, currentIndex) => ({
        currentIndex,
        previousIndex:
          currentIndex < insertedIndex
            ? currentIndex
            : currentIndex === insertedIndex
              ? null
              : currentIndex - 1,
      }));
    }
  }

  if (previousHeaders.length === currentHeaders.length + 1) {
    const removedIndex = findSingleInsertedHeaderIndex(
      currentHeaders,
      previousHeaders
    );
    if (removedIndex !== null) {
      return previousHeaders.map((_, previousIndex) => ({
        currentIndex:
          previousIndex < removedIndex
            ? previousIndex
            : previousIndex === removedIndex
              ? null
              : previousIndex - 1,
        previousIndex,
      }));
    }
  }

  const columnCount = Math.max(
    previousTable.columnCount,
    currentTable.columnCount
  );
  return Array.from({ length: columnCount }, (_, index) => ({
    currentIndex: index < currentTable.columnCount ? index : null,
    previousIndex: index < previousTable.columnCount ? index : null,
  }));
};

const findSingleInsertedHeaderIndex = (
  shorterHeaders: string[],
  longerHeaders: string[]
): number | null => {
  let shorterIndex = 0;
  let insertedIndex: number | null = null;

  for (
    let longerIndex = 0;
    longerIndex < longerHeaders.length;
    longerIndex += 1
  ) {
    if (
      shorterIndex < shorterHeaders.length &&
      shorterHeaders[shorterIndex] === longerHeaders[longerIndex]
    ) {
      shorterIndex += 1;
      continue;
    }

    if (insertedIndex !== null) {
      return null;
    }

    insertedIndex = longerIndex;
  }

  if (shorterIndex !== shorterHeaders.length) {
    return null;
  }

  return insertedIndex;
};

const findIdentifierColumnPair = (
  currentTable: NormalizedTableBlock,
  previousTable: NormalizedTableBlock
): { currentIndex: number; previousIndex: number } | null => {
  const currentIdentifier = findSingleIdentifierColumn(
    currentTable.headerTexts
  );
  const previousIdentifier = findSingleIdentifierColumn(
    previousTable.headerTexts
  );
  if (!currentIdentifier || !previousIdentifier) {
    return null;
  }

  const currentRowMap = createUniqueRowKeyMap(
    currentTable.rowTexts,
    currentIdentifier.index
  );
  const previousRowMap = createUniqueRowKeyMap(
    previousTable.rowTexts,
    previousIdentifier.index
  );
  if (!currentRowMap || !previousRowMap) {
    return null;
  }

  return {
    currentIndex: currentIdentifier.index,
    previousIndex: previousIdentifier.index,
  };
};

const findHeaderlessIdentifierColumnPair = (
  currentTable: NormalizedTableBlock,
  previousTable: NormalizedTableBlock
): { currentIndex: number; previousIndex: number } | null => {
  if (
    !currentTable.isHeaderless ||
    !previousTable.isHeaderless ||
    currentTable.columnCount !== previousTable.columnCount
  ) {
    return null;
  }

  for (let index = 0; index < currentTable.columnCount; index += 1) {
    const currentRowMap = createUniqueRowKeyMap(currentTable.rowTexts, index);
    const previousRowMap = createUniqueRowKeyMap(previousTable.rowTexts, index);
    if (!currentRowMap || !previousRowMap) {
      continue;
    }

    const hasSharedKey = Array.from(previousRowMap.keys()).some((rowKey) =>
      currentRowMap.has(rowKey)
    );
    if (hasSharedKey) {
      return {
        currentIndex: index,
        previousIndex: index,
      };
    }
  }

  return null;
};

const createPositionalRowAlignment = (
  currentTable: NormalizedTableBlock,
  previousTable: NormalizedTableBlock
): RowAlignment[] => {
  const rowCount = Math.max(
    currentTable.rows.length,
    previousTable.rows.length
  );
  return Array.from({ length: rowCount }, (_, index) => ({
    currentRow: currentTable.rows[index] ?? null,
    previousRow: previousTable.rows[index] ?? null,
  }));
};

const renderWholeTableCell = (
  cell: NormalizedTableCell | undefined,
  added: boolean,
  removed: boolean
): string => {
  if (!cell) {
    return renderBlankTableCell(added, removed);
  }

  const rawValue = cell.rawMarkdown || cell.plainText;
  if (rawValue.trim() === "") {
    return renderBlankTableCell(added, removed);
  }

  if (cell.inlineSegments && cell.inlineSegments.length > 0) {
    return renderTableCellDiff(
      added ? undefined : cell,
      added ? cell : undefined
    );
  }

  return wrapContentInSpan(rawValue, added, removed);
};

const renderBlankTableCell = (added: boolean, removed: boolean): string => {
  if (!added && !removed) {
    return "";
  }

  return `<span class="${added ? "added" : "removed"}">&nbsp;</span>`;
};

const normalizeRowKey = (value: string): string => value.trim().toLowerCase();

const emptyTable = (): NormalizedTableBlock => ({
  headerCells: [],
  headerTexts: [],
  normalizedHeaders: [],
  rows: [],
  rowTexts: [],
  rawMarkdown: "",
  headerSignature: "",
  columnCount: 0,
});
